import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, UserIcon, EnvelopeIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Button } from '@components/ui';

// Correo al que el usuario envía la solicitud para que tramitemos su firma
// electrónica. Configurable por entorno; por defecto usa el placeholder "xxxx".
const SIGNATURE_REQUEST_EMAIL =
  import.meta.env.VITE_SIGNATURE_REQUEST_EMAIL || 'xxxx@hypernovalabs.com';

interface ProfileIncompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCompleteInfo: () => void;
}

// Lista fija e informativa de requisitos que debe cumplir el perfil de la
// compañía antes de poder emitir facturas.
const REQUIREMENTS = [
  'Facturación electrónica debe estar activa para poder crear facturas',
  'Certificado de firma no puede ser nulo',
  'Certificado de autenticación no puede ser nulo',
  'Json Web Token no puede ser nulo',
  'Certificado clave no puede ser nulo',
];

export function ProfileIncompleteModal({
  isOpen,
  onClose,
  onCompleteInfo,
}: ProfileIncompleteModalProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(SIGNATURE_REQUEST_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* el navegador puede bloquear el clipboard sin permiso — se ignora */
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className="w-full max-w-xl transform overflow-hidden rounded-2xl bg-white dark:bg-secondary-800 text-left align-middle shadow-xl transition-all"
                data-testid="invoice-profile-incomplete-modal"
              >
                {/* Header */}
                <div className="relative px-6 pt-6 pb-5">
                  <button
                    type="button"
                    className="absolute right-4 top-4 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-200"
                    onClick={onClose}
                    aria-label="Cerrar"
                    data-testid="invoice-profile-incomplete-close-button"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <div className="text-center">
                    <Dialog.Title
                      as="h3"
                      className="flex items-center justify-center gap-2 text-xl font-bold text-primary-600 dark:text-primary-400"
                    >
                      <UserIcon className="w-6 h-6" />
                      Perfil Incompleto
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-primary-600 dark:text-primary-300">
                      Antes de poder facturar debes de completar los datos requeridos.
                    </p>
                  </div>
                </div>

                {/* Body: lista de requisitos */}
                <div className="border-t border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/40 px-6 py-5">
                  <ul className="space-y-3">
                    {REQUIREMENTS.map((requirement) => (
                      <li
                        key={requirement}
                        className="flex items-start gap-2 text-sm text-primary-700 dark:text-primary-300"
                      >
                        <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-primary-500" />
                        <span>{requirement}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Ayuda: tramitamos la firma electrónica por el usuario */}
                <div className="px-6 py-4">
                  <div className="flex items-start gap-3 rounded-lg border-l-4 border-primary-400 bg-primary-50/70 dark:bg-primary-900/20 px-4 py-3">
                    <EnvelopeIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500" />
                    <div>
                      <p className="text-sm font-semibold text-primary-700 dark:text-primary-200">
                        Tramitamos tu firma electrónica por ti
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-secondary-600 dark:text-secondary-300">
                        Si aún no tienes tu certificado de firma electrónica, nosotros nos
                        encargamos del trámite. Envía tu solicitud a{' '}
                        <span className="inline-flex items-center gap-1 align-baseline">
                          <a
                            href={`mailto:${SIGNATURE_REQUEST_EMAIL}`}
                            className="font-medium text-primary-600 hover:underline dark:text-primary-400 break-all"
                            data-testid="invoice-profile-incomplete-email-link"
                          >
                            {SIGNATURE_REQUEST_EMAIL}
                          </a>
                          <button
                            type="button"
                            onClick={handleCopyEmail}
                            aria-label={copied ? 'Correo copiado' : 'Copiar correo'}
                            title={copied ? '¡Copiado!' : 'Copiar correo'}
                            className="text-primary-400 hover:text-primary-600 dark:hover:text-primary-300 transition-colors"
                            data-testid="invoice-profile-incomplete-copy-email-button"
                          >
                            {copied ? (
                              <CheckIcon className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </span>{' '}
                        y nuestro equipo te guiará en el proceso.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end px-6 py-4">
                  <Button variant="primary" onClick={onCompleteInfo} data-testid="invoice-profile-incomplete-complete-button">
                    Completar Información
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
